// Parser for the "Client On-Account Charges" report (Staff Accounts upload).
//
// Layout: a metadata box, then one block per staff member — a header line
// "Name (client)" followed by charge rows and a "Total $X" line — and a grand
// total at the very end. We pull each staff member's name and their block Total,
// and ignore the grand total (it has no owning name header).
//
// pdf.js parses in a Web Worker; worker configuration lives in utils/pdfWorker
// (single owner) and the ~600 KB library is code-split, fetched on first use.

import { loadPdfjs } from "../utils/pdfWorker";
import type { StaffAccountEntry } from "./payflowStore";

async function extractLines(file: File): Promise<string[]> {
  const pdfjs = await loadPdfjs();
  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;

  const lines: string[] = [];
  for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
    const page = await doc.getPage(pageNum);
    const content = await page.getTextContent();
    // Rebuild reading order: bucket fragments by y (2pt tolerance), then sort
    // each bucket left-to-right by x.
    const buckets: Array<{ y: number; items: { x: number; str: string }[] }> = [];
    for (const item of content.items) {
      const str = item.str;
      if (!str) continue;
      const tx = item.transform || [];
      const x = tx[4] ?? 0;
      const y = tx[5] ?? 0;
      const existing = buckets.find((b) => Math.abs(b.y - y) < 2);
      if (existing) existing.items.push({ x, str });
      else buckets.push({ y, items: [{ x, str }] });
    }
    buckets.sort((a, b) => b.y - a.y);
    for (const b of buckets) {
      b.items.sort((p, q) => p.x - q.x);
      const line = b.items.map((i) => i.str).join(" ").replace(/\s+/g, " ").trim();
      if (line) lines.push(line);
    }
  }
  return lines;
}

export async function parseStaffAccountsPdf(file: File): Promise<StaffAccountEntry[]> {
  const lines = await extractLines(file);
  const entries: StaffAccountEntry[] = [];
  let currentName: string | null = null;

  for (const line of lines) {
    // Staff header: "Name (…)" — starts with a letter, no colon (excludes the
    // metadata "Store: …" line), ends with a parenthesised client tag.
    const header = line.match(/^([A-Za-z][A-Za-z.'/\- ]+?)\s*\(+.+\)+$/);
    if (header && !line.includes(":")) {
      // "(Corporate)" tags a corporate client, not a staff member — skip the
      // whole block (its Total is a corporate charge, not a payroll deduction).
      currentName = /\(\s*corporate\s*\)/i.test(line) ? null : header[1].trim();
      continue;
    }
    // Block total for the current staff member — take the last $ amount on the
    // line. Consume currentName so the grand total afterwards is ignored.
    if (currentName && /^Total\b/i.test(line)) {
      const amounts = line.match(/\$?\s*([\d,]+\.\d{2})/g);
      if (amounts && amounts.length) {
        const amount = parseFloat(amounts[amounts.length - 1].replace(/[$,\s]/g, ""));
        if (Number.isFinite(amount)) entries.push({ name: currentName, amount });
      }
      currentName = null;
    }
  }
  return entries;
}

// Match a summary employee to a parsed entry. Tolerant of the report's compound
// names (e.g. "Maurice/Siobhan Byrne/Dempsey" matches employee "Maurice Byrne")
// by requiring every token of the employee name to appear in the entry name.
const tokens = (s: string): string[] =>
  s
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);

export function staffAccountEntryIndexFor(
  employeeName: string,
  entries: StaffAccountEntry[],
): number {
  const empTokens = tokens(employeeName);
  if (empTokens.length === 0) return -1;
  return entries.findIndex((e) => {
    const entryTokens = new Set(tokens(e.name));
    return empTokens.every((t) => entryTokens.has(t));
  });
}

export function staffAccountAmountFor(
  employeeName: string,
  entries: StaffAccountEntry[],
): number | undefined {
  const idx = staffAccountEntryIndexFor(employeeName, entries);
  return idx >= 0 ? entries[idx].amount : undefined;
}
